import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Spinner, Table } from 'react-bootstrap'
import { FaDownload, FaEdit, FaInfoCircle, FaPlus, FaRedo, FaRegFileAlt } from 'react-icons/fa'
import useFetch from '../../../_fetch'
import ScheduledReportModal from './scheduled-report-modal'
import DownloadReportModal from './download-report-modal'
import { IFilterSelectorOpts_Selected, PublisherOrCampaign } from '../home/dashboard-content/types'
import { ICustomAlert } from '../alerts/custom-alerts/custom-alerts'
import '../_table/report-table.css'

export type IScheduledReport = {
    id: string
    name: string
    type: 'daily' | 'weekly' | 'monthly'
    timeZone: string
    emails: string[] 
    campaigns: PublisherOrCampaign[] | string[] | true
    publishers: PublisherOrCampaign[] | true
    channels: PublisherOrCampaign[] | true
    networks: string[] | true
    countries: string[] | true
    includePerformanceData?: boolean
    includeMonthlyPerformance?: boolean
    includeKeyMetrics?: boolean
    includeAlerts?: boolean
    alertIds?: string[]
    useCustomEmail?: boolean
    customEmailSubject?: string
    customEmailBody?: string
}

function generateRandomString() {
    return Math.random().toString(36).slice(2)
}

const ScheduledReports = () => {
    const { doFetch } = useFetch()
    const [reports, setReports] = useState<IScheduledReport[]>([])
    const [alerts, setAlerts] = useState<ICustomAlert[]>([])
    const [campaigns, setCampaigns] = useState<PublisherOrCampaign[]>([])
    const [publishers, setPublishers] = useState<PublisherOrCampaign[]>([])
    const [networks, setNetworks] = useState<string[]>([])
    const [countries, setCountries] = useState<string[]>([])
    const [channels, setChannels] = useState<PublisherOrCampaign[]>([])

    const [showModal, setShowModal] = useState(false)
    const [currentReport, setCurrentReport] = useState<IScheduledReport | null>(null)

    const [showDownloadModal, setShowDownloadModal] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)

    const loadScheduledReportData = useCallback(async () => {
        setIsLoading(true)
        setLoadError(null)

        try {
            const [relationResponse, reportsResponse, alertsResponse] = await Promise.all([
                doFetch('/data/relation-info'),
                doFetch('/scheduled-reports/get-all'),
                doFetch('/alerts/get-custom-alerts'),
            ])

            if (!relationResponse.ok || !reportsResponse.ok || !alertsResponse.ok) {
                throw new Error('Scheduled reports and their available filters could not be loaded.')
            }

            const [relationData, reportsData, alertsData] = await Promise.all([
                relationResponse.json(),
                reportsResponse.json(),
                alertsResponse.json(),
            ])

            if (!Array.isArray(reportsData.reports)) {
                throw new Error('The server returned an unexpected scheduled report format.')
            }

            setCampaigns(Array.isArray(relationData.campaigns) ? relationData.campaigns : [])
            setPublishers(Array.isArray(relationData.publishers) ? relationData.publishers : [])
            setNetworks(Array.isArray(relationData.networks) ? relationData.networks.map((item:any) => item.network) : [])
            setCountries(Array.isArray(relationData.countries) ? relationData.countries.map((item:any) => item.country) : [])
            setChannels(Array.isArray(relationData.channels) ? relationData.channels : [])
            setReports(reportsData.reports)
            setAlerts(Array.isArray(alertsData) ? alertsData : [])
        } catch (error) {
            setLoadError(error instanceof Error ? error.message : 'Scheduled reports could not be loaded.')
        } finally {
            setIsLoading(false)
        }
    }, [doFetch])

    useEffect(() => {
        void loadScheduledReportData()
    }, [loadScheduledReportData])

    const handleOpenModal = (report: IScheduledReport | null = null) => {
        setCurrentReport(report)
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentReport(null)
    }

    const handleSave = async (newReport: IScheduledReport) => {
        if (newReport.id) {
            const res = await doFetch(`/scheduled-reports/update`, { report: newReport })
            if (res.status === 200) {
                await res.json()
                setReports(currentReports => currentReports.map(report => report.id === newReport.id ? newReport : report))
            } else {
                alert('Failed to update the report.')
            }
        } else {
            const createdReport = { ...newReport, id: generateRandomString() }
            const res = await doFetch('/scheduled-reports/create', { report: createdReport })
            if (res.status === 200) {
                setReports(currentReports => [...currentReports, createdReport])
            } else {
                alert('Failed to create the report.')
            }
        }
    }

    const handleDeleteReport = async (reportId: string) => {
        const res = await doFetch(`/scheduled-reports/delete/${reportId}`)
        if (res.status === 200) {
            setReports(prevReports => prevReports.filter(report => report.id !== reportId))
        } else {
            alert('Failed to delete the report.')
        }
    }
    const handleOpenDownloadModal = () => {
        setShowDownloadModal(true)
    }

    const handleCloseDownloadModal = () => {
        setShowDownloadModal(false)
    }

    const handleDownloadReport = async (fromDate: string, toDate: string, filters: IFilterSelectorOpts_Selected, reportTitle: string, reportType: string, timeZone: string, selectedAlertIds?: string[], includeMonthlyPerformance?: boolean, includeKeyMetrics?: boolean) => {
        console.log('Downloading report for:', { fromDate, toDate, filters, reportTitle, reportType, timeZone, selectedAlertIds, includeMonthlyPerformance, includeKeyMetrics })

        const res = await doFetch('/reports/download-report', {
            reportOptions: { fromDate, toDate, reportName: reportTitle, reportType, timeZone, selectedAlertIds, includeMonthlyPerformance, includeKeyMetrics, ...filters }
        })
        if (!res.ok) throw new Error('The report could not be generated.')

        const pdfData = await res.arrayBuffer()
    
        const pdfBlob = new Blob([pdfData], { type: 'application/pdf' })
    
        const link = document.createElement('a')
        const url = URL.createObjectURL(pdfBlob)
        link.href = url
        link.download = `${reportTitle || 'report'}.pdf`
        link.click()
    
        
        URL.revokeObjectURL(url)
    
        setShowDownloadModal(false)
    }
    

    return (
        <main className="report-page-shell">
            <header className="report-page-header">
                <div className="report-page-heading">
                    <div>
                        <h3 className="mb-1">Scheduled Reports</h3>
                        <p>Create recurring email reports or download a report for a specific period.</p>
                    </div>
                </div>

                <div className="scheduled-report-actions">
                    <Button variant="outline-primary" onClick={handleOpenDownloadModal} disabled={isLoading || !!loadError}>
                        <FaDownload aria-hidden="true" />
                        Download report
                    </Button>
                    <Button variant="primary" onClick={() => handleOpenModal()} disabled={isLoading || !!loadError}>
                        <FaPlus aria-hidden="true" />
                        Add scheduled report
                    </Button>
                </div>
            </header>

            <div className="scheduled-report-info">
                <FaInfoCircle aria-hidden="true" />
                <span>
                    Scheduled reports are sent automatically by email at the selected daily, weekly, or monthly frequency.
                </span>
            </div>

            {loadError && (
                <Alert variant="danger" className="report-error-alert" role="alert">
                    <div>
                        <div className="fw-semibold">We couldn’t load scheduled reports</div>
                        <div className="small">{loadError}</div>
                    </div>
                    <Button type="button" variant="outline-danger" size="sm" onClick={loadScheduledReportData}>
                        <FaRedo aria-hidden="true" />
                        Retry
                    </Button>
                </Alert>
            )}

            {isLoading ? (
                <Card className="report-state-card border-0" aria-live="polite" aria-busy="true">
                    <Card.Body className="report-loading-state">
                        <Spinner animation="border" role="status" />
                        <div>
                            <div className="fw-semibold text-dark">Loading scheduled reports</div>
                            <div className="small text-muted">Fetching schedules, recipients, and report settings…</div>
                        </div>
                    </Card.Body>
                </Card>
            ) : !loadError && (
                <Card className="scheduled-report-table-card border-0">
                    <Table responsive hover className="scheduled-report-table">
                        <thead>
                            <tr>
                                <th>Report title</th>
                                <th>Frequency</th>
                                <th>Time zone</th>
                                <th>Recipients</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="scheduled-report-empty">
                                        <span className="report-empty-state-icon mb-2" aria-hidden="true"><FaRegFileAlt /></span>
                                        <div className="fw-semibold text-dark">No scheduled reports yet</div>
                                        <div className="small text-muted mt-1">Add a schedule to receive reports automatically by email.</div>
                                    </td>
                                </tr>
                            ) : reports.map(report => (
                                <tr key={report.id}>
                                    <td><span className="scheduled-report-name">{report.name || 'Untitled report'}</span></td>
                                    <td>
                                        <span className="scheduled-report-frequency">
                                            {report.type.charAt(0).toUpperCase() + report.type.slice(1)}
                                        </span>
                                    </td>
                                    <td><span className="text-muted">{report.timeZone || 'UTC'}</span></td>
                                    <td>
                                        <div className="scheduled-report-recipients">
                                            {Array.isArray(report.emails) && report.emails.length > 0
                                                ? report.emails.map((email, index) => (
                                                    <span className="scheduled-report-recipient" key={`${email}-${index}`}>{email}</span>
                                                ))
                                                : <span className="text-muted">No recipients</span>}
                                        </div>
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            size="sm"
                                            className="scheduled-report-edit"
                                            onClick={() => handleOpenModal(report)}
                                            aria-label={`Edit ${report.name || 'scheduled report'}`}
                                        >
                                            <FaEdit aria-hidden="true" />
                                            Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            <ScheduledReportModal
                show={showModal}
                campaigns={campaigns}
                onClose={handleCloseModal}
                onSave={handleSave}
                onDelete={handleDeleteReport}
                report={currentReport || undefined}
                countries={countries}
                networks={networks}
                publishers={publishers}
                channels={channels}
                alerts={alerts}
            />

            <DownloadReportModal
                show={showDownloadModal}
                onClose={handleCloseDownloadModal}
                onDownload={handleDownloadReport}
                campaigns={campaigns}
                countries={countries}
                networks={networks}
                publishers={publishers}
                alerts={alerts}
                channels={channels}
            />
        </main>
    )
}

export default ScheduledReports
